/* eslint-disable camelcase */
import { Button, Form, Modal, Row, message, Tooltip, Select } from 'antd'

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { StyleSheet, css } from 'aphrodite'
import { compose, withState, withStateHandlers } from 'recompose'

import { AuthenticationConsumer } from '../../contexts'

import {
  DUPLICATE_PROPERTY,
  ACCOUNT_PROPERTIES_BY_ACCOUNT_ID,
} from '../../graphql/properties.graphql'

import { withConsumer, withQueryResultAsProp } from '../../utils'
import { withMutationAsProp } from '../../utils/enhancers'
import { ACCOUNTS } from '../../graphql/accounts.graphql'

const { Option } = Select

const styles = StyleSheet.create({
  button: {
    float: 'right',
  },
  buttonCancel: {
    marginRight: '8px',
  },
  buttonRow: {
    marginTop: '35px',
  },
  formItem: {
    marginBottom: '5px',
  },
  label: {
    fontWeight: '500',
  },
})

const formItemLayout = {
  labelCol: {
    // 1600px ≤ width
    xxl: {
      span: 8,
    },
    // 1200px ≤ width < 1600px
    xl: {
      span: 8,
    },
    // 992px ≤ width < 1200px
    lg: {
      span: 8,
    },
    // 768px ≤ width < 992px
    md: {
      span: 8,
    },
    // 576px ≤ width < 768px
    sm: {
      span: 8,
    },
    // width < 576px and also default setting
    xs: {
      span: 24,
    },
  },
  wrapperCol: {
    xxl: {
      span: 16,
    },
    xl: {
      span: 16,
    },
    lg: {
      span: 16,
    },
    md: {
      span: 16,
    },
    sm: {
      span: 16,
    },
    xs: {
      span: 24,
    },
  },
}

const hasErrors = (fieldsError) =>
  Object.keys(fieldsError).some((field) => fieldsError[field])

class DuplicatePropertyModal extends Component {
  render = () => {
    const {
      form,
      property,
      account,
      isModalVisible,
      showModal,
      closeModal,
      duplicateProperty,
      accounts,
      setTargetAccountId,
    } = this.props

    const { getFieldDecorator, getFieldsError } = form

    return (
      <React.Fragment>
        <Tooltip title="Duplicate">
          <Button type="primary" icon="copy" size="large" onClick={showModal} />
        </Tooltip>
        <Modal
          title="Duplicate Project"
          visible={isModalVisible}
          onCancel={closeModal}
          width="470px"
          footer={null}
          style={{ top: '50px' }}
        >
          <Form onSubmit={this.handleAddPaletteSubmit} hideRequiredMark>
            <Form.Item
              {...formItemLayout}
              label={<span className={css(styles.label)}>Target Account</span>}
              className={css(styles.formItem)}
            >
              {getFieldDecorator('toAccount', {
                rules: [
                  {
                    required: true,
                    message: 'This field is required.',
                  },
                ],
                // Don't validate after every onChange (we will therefore only
                // validate when the user presses the "Add User" button.)
                validateTrigger: null,
                initialValue: null,
              })(
                <Select
                  onChange={(value) => {
                    setTargetAccountId(value)
                  }}
                  showSearch
                  filterOption={(input, option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Option value={null}>Please make a selection.</Option>
                  {[...accounts]
                    .sort((a, b) =>
                      a.account_name.localeCompare(b.account_name)
                    )
                    .map((account) => (
                      <Option key={account.id} value={account.id}>
                        {account.account_name}
                      </Option>
                    ))}
                </Select>
              )}
            </Form.Item>
            <Row className={css(styles.buttonRow)}>
              <Button
                className={css(styles.button)}
                type="primary"
                htmlType="submit"
                disabled={hasErrors(getFieldsError())}
                onClick={
                  () =>
                    form.validateFields(async (err, values) => {
                      const { toAccount } = values
                      if (!err) {
                        closeModal()
                        await duplicateProperty({
                          sourceAccountId: account.id,
                          destinationAccountId: toAccount,
                          propertyId: property.id,
                        })
                        message.success('Project Duplicated!')
                      }
                    }) // validateFields()
                }
              >
                Duplicate
              </Button>
              <Button
                onClick={closeModal}
                className={css(styles.button, styles.buttonCancel)}
              >
                Cancel
              </Button>
            </Row>
          </Form>
        </Modal>
      </React.Fragment>
    )
  } // render()
} // class DuplicatePropertyModal

DuplicatePropertyModal.propTypes = {
  form: PropTypes.object.isRequired,
  property: PropTypes.object.isRequired,
  account: PropTypes.object.isRequired,
  accounts: PropTypes.array.isRequired,
  duplicateProperty: PropTypes.func.isRequired,
  setTargetAccountId: PropTypes.func.isRequired,
  isModalVisible: PropTypes.bool.isRequired,
  showModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
}

const WrappedComponent = compose(
  withState('targetAccountId', 'setTargetAccountId', null),
  withConsumer(AuthenticationConsumer, { propName: 'authentication' }),
  withStateHandlers(
    ({ initialIsModalVisible = false }) => ({
      isModalVisible: initialIsModalVisible,
    }),
    {
      showModal: (/* previous state */) => (/* handler input */) => ({
        isModalVisible: true,
      }),
      closeModal: (/* previous state */) => (/* handler input */) => ({
        isModalVisible: false,
      }),
    }
  ),
  withMutationAsProp({
    gqlDocument: DUPLICATE_PROPERTY,
    mutationPropName: 'duplicateProperty',
    refetchQueries: [
      {
        gqlDocument: ACCOUNT_PROPERTIES_BY_ACCOUNT_ID,
        variables: ({ account }) => ({ accountId: account.id }),
      },
      {
        gqlDocument: ACCOUNT_PROPERTIES_BY_ACCOUNT_ID,
        variables: ({ targetAccountId }) => ({ accountId: targetAccountId }),
      },
    ],
  }),
  withQueryResultAsProp({
    gqlDocument: ACCOUNTS,
    resultPropName: 'accounts',
  }),
  Form.create()
)(DuplicatePropertyModal)

export { WrappedComponent as DuplicatePropertyModal }
